/** An app-specific, short-lived routing decision; never a media access grant. */
export interface FreeImageRoutingLease {
    readonly appID: string;
    readonly revision: string;
    readonly indexRelease: string;
    readonly expiresAt: number;
}

/** Reject stale, cross-app, malformed and excessively long server decisions. */
export function parseFreeImageRoutingLease(
    value: unknown,
    appID: string,
    now: number,
    requestStartedAt = now
): FreeImageRoutingLease | undefined {
    if (value === null || typeof value !== "object") return undefined;
    const candidate = value as Record<string, unknown>;
    if (
        candidate.enabled !== true ||
        candidate.appID !== appID ||
        typeof candidate.revision !== "string" ||
        !/^[a-zA-Z0-9_-]{1,256}$/.test(candidate.revision) ||
        typeof candidate.indexRelease !== "string" ||
        !/^[a-zA-Z0-9-]{1,128}$/.test(candidate.indexRelease) ||
        typeof candidate.validForMS !== "number" ||
        !Number.isSafeInteger(candidate.validForMS) ||
        candidate.validForMS <= 0 ||
        candidate.validForMS > 15_000 ||
        requestStartedAt > now ||
        requestStartedAt + candidate.validForMS <= now
    )
        return undefined;
    return {
        appID,
        revision: candidate.revision,
        indexRelease: candidate.indexRelease,
        expiresAt: requestStartedAt + candidate.validForMS,
    };
}

/** Expiry is independent of a pending network request, so a stalled refresh cannot retain routing. */
export class FreeImageRoutingLeaseState {
    private lease: FreeImageRoutingLease | undefined;
    private timer: ReturnType<typeof setTimeout> | undefined;
    private generation = 0;
    private stopped = false;

    constructor(private readonly appID: string, private readonly changed: () => void) {}

    public current(): FreeImageRoutingLease | undefined {
        return this.lease !== undefined && this.lease.expiresAt > Date.now() ? this.lease : undefined;
    }

    /** Only the newest refresh may install a response; stop also invalidates pending responses. */
    public async refresh(read: () => Promise<unknown>): Promise<void> {
        if (this.stopped) return;
        const generation = ++this.generation;
        // Count the entire request against the TTL; never extend a lease by network latency.
        const requestStartedAt = Date.now();
        let value: unknown;
        try {
            value = await read();
        } catch (e: unknown) {
            value = undefined;
        }
        if (this.stopped || generation !== this.generation) return;
        this.replace(parseFreeImageRoutingLease(value, this.appID, Date.now(), requestStartedAt));
    }

    /** A media failure or teardown latches this instance off; refresh cannot silently re-enable it. */
    public stop(): void {
        this.stopped = true;
        ++this.generation;
        this.replace(undefined);
    }

    private replace(lease: FreeImageRoutingLease | undefined): void {
        if (this.timer !== undefined) clearTimeout(this.timer);
        this.timer = undefined;
        const previous = this.lease;
        this.lease = lease;
        if (lease !== undefined) {
            this.timer = setTimeout(() => this.replace(undefined), Math.max(0, lease.expiresAt - Date.now()));
        }
        // A routine lease renewal does not reload already mounted media.
        if (previous?.revision !== lease?.revision || previous?.indexRelease !== lease?.indexRelease) this.changed();
    }
}
